import { BreadcrumbList, WithContext } from 'schema-dts';

import { prettifySlug } from './prettify-slug';
import config from '../../../../config';

const baseUrl = config.get('sales:url');

type BreadcrumbListItem = { item: string; name: string };

type BreadcrumbRule = {
  apply: (path: string) => Array<BreadcrumbListItem>;
  matches: RegExp | ((path: string) => boolean);
};

// WHY: Article Hub pages need a parent that exists, '/article-hub', not the programmatic parent /insurance-type/articles.
const articleHubRule: BreadcrumbRule = {
  apply: (path) => {
    const match = path.match(/^\/(\w+-insurance)\/articles\/([^/]+)$/)!;
    const articleSlug = match[2]!;
    return [
      { item: baseUrl, name: 'Home' },
      { item: `${baseUrl}/article-hub`, name: 'Article hub' },
      { item: `${baseUrl}${path}`, name: prettifySlug(articleSlug) },
    ];
  },
  matches: /^\/(\w+-insurance)\/articles\/([^/]+)$/,
};

// WHY: Car and Home Insurance privacy policy is shared across both insurance types where the combined url does not exist, so the programmatic parent is incorrect.
const carHomePrivacyPolicyRule: BreadcrumbRule = {
  apply: (path) => [
    { item: baseUrl, name: 'Home' },
    { item: `${baseUrl}/privacy-policy`, name: 'Privacy policy' },
    { item: `${baseUrl}${path}`, name: 'Car and home insurance privacy policy' },
  ],
  matches: (path) => path === '/car-and-home-insurance/privacy-policy',
};

// WHY: 'All' in /health-insurance/hospital-cover/all is not user-friendly; 'Compare hospital cover' is clearer for customers.
const comparerRule: BreadcrumbRule = {
  apply: (path) => {
    const segments = path.split('/');
    return [
      { item: baseUrl, name: 'Home' },
      { item: `${baseUrl}/${segments[1]}`, name: prettifySlug(segments[1]!) },
      { item: `${baseUrl}/${segments[1]}/${segments[2]}`, name: prettifySlug(segments[2]!) },
      { item: `${baseUrl}${path}`, name: prettifySlug(`Compare ${segments[2]!}`) },
    ];
  },
  matches: /^\/health-insurance\/([^/]+)\/all$/,
};

// WHY: Soft bundle products have two possible parents, but for SEO and user experience, 'Choose your cover' is the most relevant.
const softBundleRule: BreadcrumbRule = {
  apply: (path) => {
    const match = path.match(/^\/health-insurance\/hospital-extras-bundle\/([^/]+)$/)!;
    const productSlug = match[1]!;
    return [
      { item: baseUrl, name: 'Home' },
      { item: `${baseUrl}/health-insurance`, name: 'Health insurance' },
      { item: `${baseUrl}/health-insurance/choose-your-cover`, name: 'Choose your cover' },
      { item: `${baseUrl}${path}`, name: prettifySlug(productSlug) },
    ];
  },
  matches: /^\/health-insurance\/hospital-extras-bundle\/([^/]+)$/,
};

// Default: prettify each segment for all other paths.
const defaultRule: BreadcrumbRule = {
  apply: (path) => {
    const segments = path
      .replace(/^\/?|\/?$/g, '')
      .split('/')
      .filter(Boolean);
    return [
      { item: baseUrl, name: 'Home' },
      ...segments.map((slug, index) => ({
        item: [baseUrl, ...segments.slice(0, index + 1)].join('/'),
        name: prettifySlug(slug),
      })),
    ];
  },
  matches: () => true,
};

const breadcrumbRules: BreadcrumbRule[] = [
  articleHubRule,
  carHomePrivacyPolicyRule,
  comparerRule,
  softBundleRule,
  defaultRule, // default last as a catch-all fallback
];

const DENYLIST = ['', '404'];

const buildBreadcrumbListSchema = (path: string): WithContext<BreadcrumbList> | null => {
  if (DENYLIST.includes(path)) return null;
  const cleanedPath = ((path.split('?')[0] ?? '').split('#')[0] || path).replace(/\/$/, '');
  if (DENYLIST.includes(cleanedPath)) return null;
  const matchedRule = breadcrumbRules.find((rule) =>
    rule.matches instanceof RegExp ? rule.matches.test(cleanedPath) : rule.matches(cleanedPath),
  );
  const breadcrumbs = matchedRule?.apply(cleanedPath) ?? [];

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((breadcrumb, index) => ({
      '@type': 'ListItem',
      item: breadcrumb.item,
      name: breadcrumb.name,
      position: index + 1,
    })),
  };
};

export default buildBreadcrumbListSchema;
